import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, Eye, EyeOff, Mail, Phone, Lock, User, Check, AlertCircle } from 'lucide-react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/auth.css';
import googleIcon from '../../src/assets/icons/google-icon-logo-svgrepo-com.svg';
import laborConnectWordmark from '../../src/assets/LaborConnect Wordmark.png';
import { API_BASE_URL, TURNSTILE_SITE_KEY } from '../../src/config';
import { consumeTurnstileTokenForAction } from '../../src/utils/turnstileFlow';
import { googleStart } from '../../src/api/auth';

export default function SimpleRegistrationPage({ onBack, onAutoLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const googleSetupToken = (searchParams.get('googleSetupToken') || '').trim();
  const isGoogleSetupMode = Boolean(googleSetupToken);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [resendMsLeft, setResendMsLeft] = useState(0);
  const [googleSetupChecking, setGoogleSetupChecking] = useState(false);
  const turnstileEnabled = Boolean(TURNSTILE_SITE_KEY);

  const RESEND_COOLDOWN_MS = Number(process.env.REACT_APP_OTP_RESEND_COOLDOWN_MS ?? 60000);
  
  const [formData, setFormData] = useState({
    firstName: '',
    hasMiddleInitial: true,
    middleInitial: '',
    lastName: '',
    identifier: '',
    verificationCode: '',
    password: '',
    confirmPassword: '',
    agreedToPolicies: false,
  });

  useEffect(() => {
    const resumeState = location.state?.resumeState;
    if (!resumeState) return;

    if (Number.isInteger(resumeState.step) && resumeState.step >= 1 && resumeState.step <= 4) {
      setStep(resumeState.step);
    }
    if (resumeState.formData && typeof resumeState.formData === 'object') {
      setFormData((prev) => ({ ...prev, ...resumeState.formData }));
    }
    if (typeof resumeState.showPassword === 'boolean') setShowPassword(resumeState.showPassword);
    if (typeof resumeState.showConfirmPassword === 'boolean') setShowConfirmPassword(resumeState.showConfirmPassword);
    if (Number.isFinite(resumeState.resendMsLeft)) setResendMsLeft(Math.max(0, resumeState.resendMsLeft));

    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data;
      if (payload?.type === 'google-auth' && payload.user) {
        if (onAutoLogin) {
          onAutoLogin({
            user: payload.user,
            token: payload.token ?? null,
            expiresAt: payload.expiresAt ?? null,
            rememberMe: Boolean(payload.rememberMe),
          });
        } else {
          navigate('/dashboard');
        }
      }
      if (payload?.type === 'google-auth-error') {
        if (isGoogleSetupMode) return;
        setError(payload.message || 'Google sign-up failed. Please try again.');
      }
      if (payload?.type === 'google-auth-setup' && payload.token) {
        navigate(`/register?googleSetupToken=${encodeURIComponent(payload.token)}`);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isGoogleSetupMode, navigate, onAutoLogin]);

  useEffect(() => {
    if (!isGoogleSetupMode) {
      setGoogleSetupChecking(false);
      return;
    }

    const run = async () => {
      setGoogleSetupChecking(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/google/session?token=${encodeURIComponent(googleSetupToken)}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || 'Google setup session expired. Please try again.');
        }

        if (data?.user && onAutoLogin) {
          onAutoLogin({
            user: data.user,
            token: data.token ?? null,
            expiresAt: data.expiresAt ?? null,
            rememberMe: Boolean(data.rememberMe),
          });
          return;
        }

        if (!data?.requiresOnboarding) {
          throw new Error('Invalid Google setup session. Please try again.');
        }

        const onboarding = data.onboarding ?? {};
        setFormData((prev) => ({
          ...prev,
          firstName: onboarding.firstName || prev.firstName,
          middleInitial: onboarding.middleInitial || prev.middleInitial,
          lastName: onboarding.lastName || prev.lastName,
          identifier: onboarding.email || prev.identifier,
        }));
        setStep(3);
      } catch (err) {
        setError(err.message || 'Unable to continue Google setup. Please try again.');
      } finally {
        setGoogleSetupChecking(false);
      }
    };

    run();
  }, [API_BASE_URL, googleSetupToken, isGoogleSetupMode, onAutoLogin]);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'noMiddleInitial') {
        setFormData(prev => ({
            ...prev,
            hasMiddleInitial: !checked
        }));
         // Clear error if we are hiding the field
        if (checked) {
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.middleInitial;
                return next;
             });
        }
    } else {
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }));
        setError('');
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[name];
          return next;
        });
    }
  };

  const validateIdentifier = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const detectInputType = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return 'unknown';
    if (trimmed.includes('@')) return 'email';
    const stripped = trimmed.replace(/[\s\-()]/g, '');
    if (/^(\+?63|0)\d/.test(stripped)) return 'phone';
    if (/^\+?\d+$/.test(stripped)) return 'phone';
    return 'email';
  };

  const isValidPHPhone = (value) => {
    const stripped = value.trim().replace(/[\s\-()]/g, '');
    if (/^09\d{9}$/.test(stripped)) return true;
    if (/^\+639\d{9}$/.test(stripped)) return true;
    if (/^639\d{9}$/.test(stripped)) return true;
    return false;
  };

  const validatePassword = (pwd) => {
    return {
      length: pwd.length >= 8,
      upper: /[A-Z]/.test(pwd),
      lower: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      special: /[^A-Za-z0-9]/.test(pwd)
    };
  };

  const detectedType = detectInputType(formData.identifier);
  const registrationMethod = detectedType === 'phone' ? 'phone' : 'email';
  const passwordChecks = validatePassword(formData.password);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const buildResumeState = () => ({
    step,
    formData,
    showPassword,
    showConfirmPassword,
    resendMsLeft,
  });

  const redirectToHumanCheckForGoogle = () => {
    navigate('/human-check', {
      state: {
        nextPath: `/register${location.search || ''}`,
        requiredAction: 'login_start',
        resumeState: buildResumeState(),
      },
    });
  };

  const handleGoogleContinue = async () => {
    if (loading) return;

    const turnstileToken = turnstileEnabled
      ? consumeTurnstileTokenForAction('login_start')
      : '';

    if (turnstileEnabled && !turnstileToken) {
      redirectToHumanCheckForGoogle();
      return;
    }

    setLoading(true);
    setError('');
    try {
      const origin = window.location?.origin || '';
      const { data } = await googleStart({
        origin,
        turnstileToken: turnstileEnabled ? turnstileToken : undefined,
      });

      const authUrl = typeof data?.authUrl === 'string' ? data.authUrl : '';
      if (!authUrl) {
        throw new Error('Failed to start Google sign-up.');
      }

      const popup = window.open(
        authUrl,
        'google-oauth',
        'width=500,height=650,menubar=no,toolbar=no,location=no,status=no'
      );
      if (!popup) {
        window.location.href = authUrl;
      }
    } catch (err) {
      const status = Number(err?.response?.status || 0);
      const message = String(err?.response?.data?.message || '').toLowerCase();
      if (turnstileEnabled && (status === 400 || status === 403) && message.includes('security verification')) {
        redirectToHumanCheckForGoogle();
        return;
      }
      setError(err?.response?.data?.message || err.message || 'Google sign-up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (resendMsLeft <= 0) return undefined;
    const interval = setInterval(() => {
      setResendMsLeft((prev) => Math.max(prev - 1000, 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendMsLeft]);

  const sendCode = async ({ advanceStep }) => {
    if (resendMsLeft > 0) return;

    const turnstileToken = turnstileEnabled
      ? consumeTurnstileTokenForAction('register_request_code')
      : '';

    if (turnstileEnabled && !turnstileToken) {
      navigate('/human-check', {
        state: {
          nextPath: `/register${location.search || ''}`,
          requiredAction: 'register_request_code',
          resumeState: {
            step,
            formData,
            showPassword,
            showConfirmPassword,
            resendMsLeft,
          },
        },
      });
      return;
    }

    setLoading(true);
    setError('');
    try {
      const requestBody = {};
      if (registrationMethod === 'phone') {
        requestBody.phone = formData.identifier.trim();
      } else {
        requestBody.email = formData.identifier.trim();
      }
      const res = await fetch(`${API_BASE_URL}/api/auth/quick-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...requestBody,
          turnstileToken: turnstileEnabled ? turnstileToken : undefined,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send code');
      setResendMsLeft(RESEND_COOLDOWN_MS);
      if (advanceStep) {
        setStep(2);
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.identifier;
          return next;
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!validateStep1()) {
      return;
    }
    await sendCode({ advanceStep: true });
  };

  const handleResendCode = async () => {
    if (!validateStep1()) {
      return;
    }
    await sendCode({ advanceStep: false });
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!validateStep2()) {
      return;
    }
    setLoading(true);
    setError('');
    try {
      const verifyBody = { code: formData.verificationCode };
      if (registrationMethod === 'phone') {
        verifyBody.phone = formData.identifier.trim();
      } else {
        verifyBody.email = formData.identifier.trim();
      }
      const res = await fetch(`${API_BASE_URL}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(verifyBody)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Verification failed');
      setStep(3);
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next.verificationCode;
        return next;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!validateStep3()) {
      return;
    }
    setStep(4);
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.firstName;
      delete next.middleInitial;
      delete next.lastName;
      return next;
    });
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!validateStep4()) {
      if (!formData.agreedToPolicies) {
        setError('You must agree to the Data Privacy Policy and Terms & Conditions before creating an account.');
      }
      return;
    }
    if (!formData.agreedToPolicies) {
      setError('You must agree to the Data Privacy Policy and Terms & Conditions before creating an account.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!isPasswordValid) {
      setError("Please meet all password requirements");
      return;
    }
    const turnstileToken = turnstileEnabled
      ? (consumeTurnstileTokenForAction('register_request_code')
        || consumeTurnstileTokenForAction('register_create_account'))
      : '';

    setLoading(true);
    setError('');
    try {
      if (isGoogleSetupMode) {
        const res = await fetch(`${API_BASE_URL}/api/auth/google/complete-setup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: googleSetupToken,
            firstName: formData.firstName,
            middleInitial: formData.hasMiddleInitial ? formData.middleInitial : '',
            lastName: formData.lastName,
            password: formData.password,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Google setup failed');

        if (data.user && data.token && onAutoLogin) {
          localStorage.setItem('alu_token', data.token);
          onAutoLogin({
            user: data.user,
            token: data.token,
            expiresAt: data.expiresAt ?? null,
            rememberMe: false,
          });
        } else {
          navigate('/login');
        }
        return;
      }

      // 1. Set Password
      const setPasswordBody = {
        password: formData.password,
        firstName: formData.firstName,
        middleInitial: formData.hasMiddleInitial ? formData.middleInitial : '',
        lastName: formData.lastName,
        turnstileToken: turnstileEnabled ? turnstileToken : undefined,
      };
      if (registrationMethod === 'phone') {
        setPasswordBody.phone = formData.identifier.trim();
      } else {
        setPasswordBody.email = formData.identifier.trim();
      }

      const res = await fetch(`${API_BASE_URL}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(setPasswordBody)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Registration failed');

      // Auto-login: if backend returned token + user, skip login page
      if (data.user && data.token && onAutoLogin) {
        localStorage.setItem('alu_token', data.token);
        onAutoLogin({
          user: data.user,
          token: data.token,
          expiresAt: data.expiresAt ?? null,
          rememberMe: false,
        });
      } else {
        navigate('/login');
      }

    } catch (err) {
      if (turnstileEnabled && String(err?.message || '').toLowerCase().includes('security verification')) {
        navigate('/human-check', {
          state: {
            nextPath: `/register${location.search || ''}`,
            requiredAction: 'register_request_code',
            resumeState: {
              step,
              formData,
              showPassword,
              showConfirmPassword,
              resendMsLeft,
            },
          },
        });
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    if (isGoogleSetupMode && googleSetupChecking) {
      e.preventDefault();
      return;
    }

    if (!isGoogleSetupMode && step === 1) {
      await handleSendCode(e);
      return;
    }

    if (!isGoogleSetupMode && step === 2) {
      await handleVerifyCode(e);
      return;
    }

    if (step === 3) {
      handleNameSubmit(e);
      return;
    }

    if (step === 4) {
      await handleCreateAccount(e);
      return;
    }

    e.preventDefault();
  };

  const renderStep1 = () => (
    <div className="auth-card__step">
      <div className="auth-card__field">
        <label htmlFor="identifier">Email or phone number</label>
        <div className={`auth-card__input ${fieldErrors.identifier ? 'auth-card__input--error' : ''}`}>
          {detectedType === 'phone' ? <Phone size={18} /> : detectedType === 'email' ? <Mail size={18} /> : <User size={18} />}
            <input
            id="identifier"
            name="identifier"
            type="text"
              placeholder="juan.delacruz@gmail.com or +63 900 000 0000"
            value={formData.identifier}
            onChange={handleChange}
            required
            autoFocus
          />
        </div>
        {fieldErrors.identifier && (
          <div className="auth-card__field-error">
            <AlertCircle size={14} />
            <span>{fieldErrors.identifier}</span>
          </div>
        )}
      </div>

      <button
        type="button"
        className="auth-card__submit"
        onClick={handleSendCode}
        disabled={loading}
      >
        {loading ? 'Sending...' : 'Send Verification Code'}
      </button>

      <div className="auth-card__divider">
        <span>or</span>
      </div>

      <button
        type="button"
        className="auth-card__google-btn"
        onClick={handleGoogleContinue}
        disabled={loading}
      >
        <span className="auth-card__google-icon" aria-hidden="true">
          <img src={googleIcon} alt="" />
        </span>
        Continue with Google
      </button>

    </div>
  );

  const applyFieldErrors = (fields, messages) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        if (messages[field]) {
          next[field] = messages[field];
        } else {
          delete next[field];
        }
      });
      return next;
    });
  };

  const validateStep1 = () => {
    const messages = {};
    const trimmed = formData.identifier.trim();
    if (!trimmed) {
      messages.identifier = 'Email or phone number is required.';
    } else if (detectedType === 'email') {
      if (!validateIdentifier(trimmed)) {
        messages.identifier = 'Enter a valid email address.';
      }
    } else if (detectedType === 'phone') {
      if (!isValidPHPhone(trimmed)) {
        messages.identifier = 'Enter a valid phone number.';
      }
    }
    applyFieldErrors(['identifier'], messages);
    return Object.keys(messages).length === 0;
  };

  const validateStep2 = () => {
    const messages = {};
    const code = formData.verificationCode.trim();
    if (!code) {
      messages.verificationCode = 'Verification code is required.';
    } else if (!/^\d{6}$/.test(code)) {
      messages.verificationCode = `Enter the 6-digit code sent to your ${registrationMethod === 'phone' ? 'phone number' : 'email'}.`;
    }
    applyFieldErrors(['verificationCode'], messages);
    return Object.keys(messages).length === 0;
  };

  const validateStep3 = () => {
    const messages = {};
    if (!formData.firstName.trim()) {
      messages.firstName = 'First name is required.';
    }
    if (formData.hasMiddleInitial && !formData.middleInitial.trim()) {
      messages.middleInitial = 'Middle name is required.';
    }
    if (!formData.lastName.trim()) {
      messages.lastName = 'Last name is required.';
    }
    applyFieldErrors(['firstName', 'middleInitial', 'lastName'], messages);
    return Object.keys(messages).length === 0;
  };

  const validateStep4 = () => {
    const messages = {};
    if (!formData.password) {
      messages.password = 'Password is required.';
    } else if (!isPasswordValid) {
      messages.password = 'Password must meet all requirements.';
    }
    if (!formData.confirmPassword) {
      messages.confirmPassword = 'Please confirm your password.';
    } else if (formData.password !== formData.confirmPassword) {
      messages.confirmPassword = 'Passwords do not match.';
    }
    applyFieldErrors(['password', 'confirmPassword'], messages);
    return Object.keys(messages).length === 0;
  };

  const renderStep2 = () => (
    <div className="auth-card__step">
      <div className="auth-card__field">
        <label htmlFor="verificationCode">Verification Code</label>
        <p className="auth-card__hint">Enter the 6-digit code sent to {formData.identifier}</p>
        <div className={`auth-card__input ${fieldErrors.verificationCode ? 'auth-card__input--error' : ''}`}>
          <Lock size={18} />
          <input
            id="verificationCode"
            name="verificationCode"
            type="text"
            placeholder="000000"
            value={formData.verificationCode}
            onChange={handleChange}
            required
            maxLength={6}
            autoFocus
          />
        </div>
        {fieldErrors.verificationCode && (
          <div className="auth-card__field-error">
            <AlertCircle size={14} />
            <span>{fieldErrors.verificationCode}</span>
          </div>
        )}
      </div>

      <div className="auth-card__actions">
        <button type="button" className="auth-card__back-btn" onClick={() => setStep(1)}>
          Back
        </button>
        <button 
          type="button" 
          className="auth-card__submit" 
          onClick={handleVerifyCode} 
          disabled={loading || formData.verificationCode.length < 6}
        >
          {loading ? 'Verifying...' : 'Verify Code'}
        </button>
      </div>

      <div className="auth-card__actions" style={{ gridTemplateColumns: '1fr', textAlign: 'center' }}>
        <button
          type="button"
          className="auth-card__back-btn"
          onClick={handleResendCode}
          disabled={loading || resendMsLeft > 0}
        >
          {resendMsLeft > 0
            ? `Resend in ${Math.ceil(resendMsLeft / 1000)}s`
            : 'Resend Code'}
        </button>
      </div>

    </div>
  );

  const renderStep3 = () => (
    <div className="auth-card__step">
      <div className="auth-card__field">
        <label htmlFor="firstName">First Name</label>
        <div className={`auth-card__input ${fieldErrors.firstName ? 'auth-card__input--error' : ''}`}>
          <User size={18} />
          <input
            id="firstName"
            name="firstName"
            type="text"
            placeholder="Enter your first name"
            value={formData.firstName}
            onChange={handleChange}
            required
            autoFocus
          />
        </div>
        {fieldErrors.firstName && (
          <div className="auth-card__field-error">
            <AlertCircle size={14} />
            <span>{fieldErrors.firstName}</span>
          </div>
        )}
      </div>

      <div className="auth-card__checkbox-wrapper">
        <label className="auth-card__checkbox">
          <input
            type="checkbox"
            name="noMiddleInitial"
            checked={!formData.hasMiddleInitial}
            onChange={handleChange}
          />
          <span>I don't have a middle name</span>
        </label>
      </div>

      {formData.hasMiddleInitial && (
        <div className="auth-card__field">
          <label htmlFor="middleInitial">Middle Name</label>
          <div className={`auth-card__input ${fieldErrors.middleInitial ? 'auth-card__input--error' : ''}`}>
            <User size={18} />
            <input
              id="middleInitial"
              name="middleInitial"
              type="text"
              placeholder="Enter your middle name"
              value={formData.middleInitial}
              onChange={handleChange}
            />
          </div>
          {fieldErrors.middleInitial && (
            <div className="auth-card__field-error">
              <AlertCircle size={14} />
              <span>{fieldErrors.middleInitial}</span>
            </div>
          )}
        </div>
      )}

      <div className="auth-card__field">
        <label htmlFor="lastName">Last Name (Surname)</label>
        <div className={`auth-card__input ${fieldErrors.lastName ? 'auth-card__input--error' : ''}`}>
          <User size={18} />
          <input
            id="lastName"
            name="lastName"
            type="text"
            placeholder="Enter your last name"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
        </div>
        {fieldErrors.lastName && (
          <div className="auth-card__field-error">
            <AlertCircle size={14} />
            <span>{fieldErrors.lastName}</span>
          </div>
        )}
      </div>

      <div className="auth-card__actions">
        <button type="button" className="auth-card__back-btn" onClick={() => setStep(2)}>
          Back
        </button>
        <button 
          type="button" 
          className="auth-card__submit" 
          onClick={handleNameSubmit} 
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="auth-card__step">
      <div className="auth-card__field">
        <label htmlFor="password">Password</label>
        <div className={`auth-card__input ${fieldErrors.password ? 'auth-card__input--error' : ''}`}>
          <Lock size={18} />
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="Create a password"
            value={formData.password}
            onChange={handleChange}
            required
            autoFocus
          />
          <button
            type="button"
            className="auth-card__password-toggle"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {fieldErrors.password && (
          <div className="auth-card__field-error">
            <AlertCircle size={14} />
            <span>{fieldErrors.password}</span>
          </div>
        )}
      </div>

      <div className="auth-card__password-reqs">
        <div className={`req-item ${passwordChecks.length ? 'met' : ''}`}>
          {passwordChecks.length ? <Check size={14} /> : <div className="dot" />}
          At least 8 characters
        </div>
        <div className={`req-item ${passwordChecks.upper ? 'met' : ''}`}>
          {passwordChecks.upper ? <Check size={14} /> : <div className="dot" />}
          Uppercase letter (A-Z)
        </div>
        <div className={`req-item ${passwordChecks.lower ? 'met' : ''}`}>
          {passwordChecks.lower ? <Check size={14} /> : <div className="dot" />}
          Lowercase letter (a-z)
        </div>
        <div className={`req-item ${passwordChecks.number ? 'met' : ''}`}>
          {passwordChecks.number ? <Check size={14} /> : <div className="dot" />}
          Number (0-9)
        </div>
        <div className={`req-item ${passwordChecks.special ? 'met' : ''}`}>
          {passwordChecks.special ? <Check size={14} /> : <div className="dot" />}
          Special character (!@#$%)
        </div>
      </div>

      <div className="auth-card__field">
        <label htmlFor="confirmPassword">Confirm Password</label>
        <div className={`auth-card__input ${fieldErrors.confirmPassword ? 'auth-card__input--error' : ''}`}>
          <Lock size={18} />
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm your password"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />
          <button
            type="button"
            className="auth-card__password-toggle"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {fieldErrors.confirmPassword && (
          <div className="auth-card__field-error">
            <AlertCircle size={14} />
            <span>{fieldErrors.confirmPassword}</span>
          </div>
        )}
      </div>

      <div className="auth-card__checkbox-wrapper auth-card__checkbox-wrapper--legal">
        <label className="auth-card__checkbox auth-card__checkbox--legal" htmlFor="agreedToPolicies">
          <input
            id="agreedToPolicies"
            type="checkbox"
            name="agreedToPolicies"
            checked={formData.agreedToPolicies}
            onChange={handleChange}
          />
          <span>
            I agree to the{' '}
            <Link to="/privacy-policy" className="auth-card__inline-link" target="_blank" rel="noopener noreferrer">
              Data Privacy Policy
            </Link>{' '}
            and{' '}
            <Link to="/terms-and-conditions" className="auth-card__inline-link" target="_blank" rel="noopener noreferrer">
              Terms &amp; Conditions
            </Link>
          </span>
        </label>
      </div>

      <div className="auth-card__actions">
        <button type="button" className="auth-card__back-btn" onClick={() => setStep(3)}>
          Back
        </button>
        <button 
          type="submit" 
          className="auth-card__submit" 
          onClick={handleCreateAccount}
          disabled={loading || !isPasswordValid || !formData.confirmPassword}
        >
          {loading ? 'Creating...' : 'Create Account'}
        </button>
      </div>

    </div>
  );

  return (
    <div className="auth-page">
      <header className="auth-page__header">
        <div className="auth-page__header-inner">
          <button type="button" className="auth-page__back" onClick={step === 1 ? onBack : () => setStep(step - 1)}>
            <ArrowLeft size={18} />
          </button>
          <div className="auth-page__title">
            <div>
              <h1>Create</h1>
              <p>Secure member onboarding</p>
            </div>
          </div>
        </div>
      </header>

      <main className="auth-page__body">
        <div className="auth-page__inner">
          <section className="auth-page__intro">
            <div className="auth-page__badge">Membership Registration</div>
            <h2>
              Introducing
              <img src={laborConnectWordmark} alt="LaborConnect" className="auth-page__wordmark" />
            </h2>
            <p>
              Sign in using your registered email or mobile number to access your Digital ID, submit requests and
              assistance concerns, manage venue bookings, and use AI-powered features. Your login is protected using
              industry standard encryption.
            </p>
          </section>

          <section className="auth-page__form">
            <div className="auth-card">
              <div className="auth-card__header">
                <h2>
                  {step === 1 && (isGoogleSetupMode ? 'Continue Google Setup' : 'Start Your Registration')}
                  {step === 2 && (isGoogleSetupMode ? 'Continue Google Setup' : 'Verify Your Account')}
                  {step === 3 && 'Basic Information'}
                  {step === 4 && 'Secure Your Account'}
                </h2>
                <p>
                  {step === 1 && (isGoogleSetupMode ? 'Validating your Google sign-in session' : 'Enter your email address or phone number to get started')}
                  {step === 2 && (isGoogleSetupMode ? 'Preparing your account details' : `We sent a code to your ${registrationMethod === 'phone' ? 'phone number' : 'email address'}`)}
                  {step === 3 && 'Tell us a bit about yourself'}
                  {step === 4 && 'Set a strong password for your account'}
                </p>
              </div>

              <div className="auth-card__progress">
                <div className={`auth-card__progress-step ${step >= (isGoogleSetupMode ? 3 : 1) ? 'active' : ''}`} />
                <div className={`auth-card__progress-step ${step >= (isGoogleSetupMode ? 3 : 2) ? 'active' : ''}`} />
                <div className={`auth-card__progress-step ${step >= 3 ? 'active' : ''}`} />
                <div className={`auth-card__progress-step ${step >= 4 ? 'active' : ''}`} />
              </div>

              <form className="auth-card__form" onSubmit={handleFormSubmit}>
                {error && (
                  <div className="auth-card__alert auth-card__alert--error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                {isGoogleSetupMode && googleSetupChecking ? (
                  <div className="auth-card__step">
                    <p className="auth-card__hint">Validating Google setup session...</p>
                  </div>
                ) : null}

                {!isGoogleSetupMode && step === 1 && renderStep1()}
                {!isGoogleSetupMode && step === 2 && renderStep2()}
                {step === 3 && renderStep3()}
                {step === 4 && renderStep4()}
              </form>

              {!isGoogleSetupMode && step === 1 && (
                <div className="auth-card__footer">
                  <span>Already have an account?</span>
                  <Link to="/login">Sign In</Link>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      <footer className="auth-page__footer">
        <p>Need help? Contact your union representative</p>
        <p>Labor Connect - Luzon regional</p>
      </footer>
    </div>
  );
}

SimpleRegistrationPage.propTypes = {
  onBack: PropTypes.func.isRequired,
  onAutoLogin: PropTypes.func,
};

SimpleRegistrationPage.defaultProps = {
  onAutoLogin: null,
};

